import React, { useEffect, useState } from "react";
import {
  Form,
  Button,
  Input,
  InputNumber,
  message,
  Select,
  Upload,
  Card,
  Row,
  Col,
  Typography,
  Divider,
} from "antd";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../../shared/api/AllApi";
import {
  invalidateCatalog,
  useCategoriesCatalog,
  useUnitsCatalog,
} from "../../../hooks/useCatalogData";
import { parseLocalizedName } from "../../../utils/localization";

const { Title } = Typography;
const { Option } = Select;

const AddProducts = () => {
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [subUnits, setSubUnits] = useState([]);
  const [showSubUnit, setShowSubUnit] = useState(false);

  const { data: storeUnits } = useUnitsCatalog();
  const { data: categoriesRaw } = useCategoriesCatalog();

  const categories = categoriesRaw.map((cat) => ({
    id: cat.id,
    name: parseLocalizedName(cat.name, i18n.language, "Unknown"),
  }));

  const parseUnitName = (raw) => {
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return parsed.en || parsed.ar || parsed.ku || String(raw);
    } catch {
      return String(raw || "");
    }
  };

  const handleUnitChange = async (value) => {
    setSubUnits([]);
    setShowSubUnit(false);
    form.setFieldsValue({ sub_unit_id: undefined, sub_unit_ratio: 1, sub_unit_price: undefined });
    if (!value) return;
    try {
      const res = await apiClient.get(`/units/${value}/sub`);
      const list = res.data || [];
      setSubUnits(list);
      setShowSubUnit(list.length > 0);
    } catch {
      setSubUnits([]);
    }
  };

  const handleSubUnitChange = (value) => {
    const sub = subUnits.find((s) => s.id === value);
    const ratio = sub?.ratio ? Number(sub.ratio) : 1;
    form.setFieldsValue({ sub_unit_ratio: ratio });
    const price = form.getFieldValue("price");
    if (price && ratio > 0) {
      form.setFieldsValue({ sub_unit_price: Number(price) / ratio });
    }
  };

  useEffect(() => {
    const price = form.getFieldValue("price");
    const ratio = form.getFieldValue("sub_unit_ratio");
    if (showSubUnit && price && ratio > 0) {
      form.setFieldsValue({ sub_unit_price: Number(price) / Number(ratio) });
    }
  });

  const handleImageUpload = async ({ file }) => {
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post("/upload-file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.status === 200) {
        setImageUrl(res.data.path);
      } else {
        message.error(t("errorMessage"));
        setImagePreview(null);
      }
    } catch {
      message.error(t("errorMessage"));
      setImagePreview(null);
    } finally {
      setImageUploading(false);
      URL.revokeObjectURL(previewUrl);
    }
    return false;
  };

  const handleSubmit = async (values) => {
    if (!values.unit_id) {
      message.error(t("Please select a unit"));
      return;
    }
    try {
      setSubmitting(true);
      const productData = {
        unit_id: values.unit_id,
        name: JSON.stringify({ en: values.name_ku || "", ar: values.name_ar || "" }),
        price: values.price || 0,
        sku: values.sku || "0",
        barcode: values.barcode?.trim() || null,
        stock: values.stock || 0,
        weight: values.weight || 0,
      };
      if (values.category_id) productData.category_id = values.category_id;
      if (imageUrl) productData.image_url = imageUrl;
      if (values.sub_unit_id) {
        productData.sub_unit_id = values.sub_unit_id;
        productData.sub_unit_ratio = values.sub_unit_ratio || 1;
        productData.sub_unit_price = values.sub_unit_price || 0;
      }

      await apiClient.post("/products", productData);
      invalidateCatalog("products");
      message.success(t("successMessage"));
      navigate("/GetProducts");
    } catch (error) {
      const msg = error?.response?.data?.message || t("errorMessage");
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setImageUrl(null);
    setImagePreview(null);
    setSubUnits([]);
    setShowSubUnit(false);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <Title level={3}>{t("add products")}</Title>
      </div>

      <Card className="form-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark
          scrollToFirstError
        >
          {/* Image upload */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={({ file }) => handleImageUpload({ file: file.originFileObj || file })}
              disabled={imageUploading}
            >
              {imagePreview || imageUrl ? (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <img
                    src={imagePreview || imageUrl}
                    alt="product"
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 12,
                      border: "2px dashed #d1d5db",
                      cursor: "pointer",
                    }}
                  />
                </div>
              ) : (
                <Button icon={<UploadOutlined />} loading={imageUploading}>
                  {t("image")}
                </Button>
              )}
            </Upload>
          </div>

          <Row gutter={[16, 0]}>
            {/* Kurdish name */}
            <Col xs={24} md={12}>
              <Form.Item name="name_ku" label={t("كوردى")}>
                <Input placeholder={t("enter name product")} />
              </Form.Item>
            </Col>
            {/* Arabic name */}
            <Col xs={24} md={12}>
              <Form.Item name="name_ar" label={t("عربي")}>
                <Input placeholder={t("enter name product")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name="category_id" label={t("Category")}>
                <Select
                  placeholder={t("Select category")}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {categories.map((cat) => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="unit_id"
                label={t("unit")}
                rules={[{ required: true, message: t("Please select a unit") }]}
              >
                <Select
                  placeholder={t("حدد الوحدة")}
                  onChange={handleUnitChange}
                  allowClear
                >
                  {storeUnits.map((u) => (
                    <Option key={u.id} value={u.id}>
                      {parseUnitName(u.name)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={8}>
              <Form.Item name="price" label={t("price")}>
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: "100%" }}
                  placeholder={t("أدخل السعر")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="stock" label={t("stock")}>
                <InputNumber min={0} style={{ width: "100%" }} placeholder={t("أدخل المخزون")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="weight" label={t("کێش")}>
                <InputNumber
                  min={0}
                  step={0.01}
                  addonAfter="KG"
                  style={{ width: "100%" }}
                  placeholder={t("کێش بە کیلۆگرام داخڵ بکە")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name="sku" label={t("sku")}>
                <Input placeholder={t("أدخل رقم SKU الخاص بك")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="barcode" label={t("productBarcode")}>
                <Input placeholder={t("productBarcodePlaceholder")} />
              </Form.Item>
            </Col>
          </Row>

          {showSubUnit && (
            <>
              <Divider>{t("یەکەی ژێر")}</Divider>
              <Row gutter={[16, 0]}>
                <Col xs={24} md={8}>
                  <Form.Item name="sub_unit_id" label={t("یەکەی ژێر")}>
                    <Select
                      placeholder={t("Select sub unit")}
                      allowClear
                      onChange={handleSubUnitChange}
                    >
                      {subUnits.map((sub) => (
                        <Option key={sub.id} value={sub.id}>
                          {parseUnitName(sub.name)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="sub_unit_ratio" label={t("پێوانەی یەکە")} initialValue={1}>
                    <InputNumber min={0.001} step={0.01} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="sub_unit_price" label={t("نرخی یەک ژێر یەکە")}>
                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Form.Item style={{ marginTop: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              icon={<PlusOutlined />}
              style={{ marginInlineEnd: 8 }}
            >
              {t("submit")}
            </Button>
            <Button onClick={handleReset} style={{ marginInlineEnd: 8 }}>
              {t("reset")}
            </Button>
            <Button danger onClick={() => navigate("/GetProducts")}>
              {t("cancel")}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AddProducts;
